// Wayne AI Platform — locked WebSocket envelope (v:1).
//
// Sourced verbatim from FRONTEND_PLAN.md §11. Two discriminated unions:
//   - ServerEvent: what the server pushes to the SPA.
//   - ClientEvent: what the SPA sends back (subscribe / replay / ack).
//
// All shapes are validated at runtime via Zod so a buggy backend can't crash
// the reducer with unexpected payloads.

import { z } from 'zod';

// -- AgentId -----------------------------------------------------------------

export const AgentIdSchema = z.enum([
  'developer',
  'manager',
  'project-manager',
  'qa-testing',
  'devops',
  'security',
  'data-engineer',
  'support-engineer',
  'analyst',
]);

export type AgentId = z.infer<typeof AgentIdSchema>;

// -- AgentPhase --------------------------------------------------------------

export const AgentPhaseSchema = z.enum([
  'idle',
  'thinking',
  'tool',
  'streaming',
  'success',
  'error',
]);

export type AgentPhase = z.infer<typeof AgentPhaseSchema>;

// -- Event types --------------------------------------------------------------

export const ServerEventTypeSchema = z.enum([
  'agent.phase',
  'agent.tool',
  'agent.token',
  'agent.message',
  'agent.error',
  'agent.observation',
  'graph.edge',
  'system.heartbeat',
  // Snapshot fallback when the server detects the gap is too large to replay.
  'system.snapshot',
  'memory.extracted',
  'memory.promoted',
]);

export type ServerEventType = z.infer<typeof ServerEventTypeSchema>;

// -- Payload shapes -----------------------------------------------------------

export const PhasePayloadSchema = z.object({
  phase: AgentPhaseSchema,
});

export const ToolPayloadSchema = z.object({
  toolName: z.string().min(1),
  status: z.enum(['pending', 'success', 'error']),
  durationMs: z.number().int().nonnegative().optional(),
});

export const TokenPayloadSchema = z.object({
  // `n` is a delta (count of new tokens), NOT cumulative.
  n: z.number().int().positive(),
  text: z.string().optional(),
});

export const MessagePayloadSchema = z.object({
  messageId: z.string().min(1),
  role: z.enum(['assistant', 'tool', 'system']),
  contentPreview: z.string().max(2048),
  cumulativeText: z.string().optional(),
  finalize: z.boolean().optional(),
});

export const ErrorPayloadSchema = z.object({
  code: z.string().min(1),
  // The user-safe message; we never log full stack traces here.
  message: z.string().max(512),
});

export const GraphEdgePayloadSchema = z.object({
  from: AgentIdSchema,
  to: AgentIdSchema,
  kind: z.enum(['handoff', 'message']),
});

export const HeartbeatPayloadSchema = z.object({}).strict();

export const SnapshotPayloadSchema = z.object({
  // Server-published snapshot of the canonical state for `sessionId`.
  // Opaque from the SPA's perspective other than ensuring `lastSeq` is set.
  lastSeq: z.number().int().nonnegative(),
  agents: z.unknown(),
  edges: z.unknown(),
});

export const MemoryExtractedPayloadSchema = z.object({
  memories: z.array(z.object({
    memoryId: z.string(),
    category: z.string(),
    content: z.string(),
    confidence: z.number(),
    autoPromote: z.boolean(),
  })),
});

export const MemoryPromotedPayloadSchema = z.object({
  memoryId: z.string(),
  previousTier: z.string(),
  newTier: z.string(),
  content: z.string().optional(),
});

export const ObservationPayloadSchema = z.object({
  observationId: z.string(),
  text: z.string(),
});

export type MemoryExtractedPayload = z.infer<typeof MemoryExtractedPayloadSchema>;
export type MemoryPromotedPayload = z.infer<typeof MemoryPromotedPayloadSchema>;
export type ObservationPayload = z.infer<typeof ObservationPayloadSchema>;

// -- ServerEvent (full discriminated union) ----------------------------------

const BaseServerEvent = {
  v: z.literal(1),
  seq: z.number().int().nonnegative(),
  ts: z.number().int().nonnegative(),
  sessionId: z.string().min(1),
  agentId: AgentIdSchema,
};

export const ServerEventSchema = z.discriminatedUnion('type', [
  z.object({ ...BaseServerEvent, type: z.literal('agent.phase'), payload: PhasePayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('agent.tool'), payload: ToolPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('agent.token'), payload: TokenPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('agent.message'), payload: MessagePayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('agent.error'), payload: ErrorPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('graph.edge'), payload: GraphEdgePayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('system.heartbeat'), payload: HeartbeatPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('system.snapshot'), payload: SnapshotPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('memory.extracted'), payload: MemoryExtractedPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('memory.promoted'), payload: MemoryPromotedPayloadSchema }),
  z.object({ ...BaseServerEvent, type: z.literal('agent.observation'), payload: ObservationPayloadSchema }),
]);

export type ServerEvent = z.infer<typeof ServerEventSchema>;

// -- ClientEvent --------------------------------------------------------------

export const ClientEventSchema = z.object({
  v: z.literal(1),
  type: z.enum(['subscribe', 'unsubscribe', 'replay', 'ack']),
  sessionId: z.string().min(1).optional(),
  fromSeq: z.number().int().nonnegative().optional(),
});

export type ClientEvent = z.infer<typeof ClientEventSchema>;

// -- Helpers -----------------------------------------------------------------

/**
 * Parse a raw frame from the server. Returns the typed event on success and
 * `null` on any validation failure — callers should drop the frame and log
 * (we never let a bad envelope crash the chat surface).
 */
export function decodeServerEvent(raw: unknown): ServerEvent | null {
  const parsed = ServerEventSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Encode a client event for sending. Throws if the input is invalid. */
export function encodeClientEvent(event: ClientEvent): string {
  return JSON.stringify(ClientEventSchema.parse(event));
}

/** WebSocket close code that the server uses when auth fails — 4401 mirrors
 * HTTP 401 in the application range. The SPA must handle it identically:
 * full-page redirect to Midway. */
export const WS_CLOSE_CODE_UNAUTHORIZED = 4401;

/** Snapshot is required when the gap from `lastSeq` exceeds this threshold. */
export const SNAPSHOT_GAP_THRESHOLD = 200;

/** Server emits a heartbeat every 25s. Client tolerates a 10s grace. */
export const HEARTBEAT_INTERVAL_MS = 25_000;
export const HEARTBEAT_GRACE_MS = 10_000;
